import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  getStudentPresentation,
  postStudentPresentationComplete,
  postStudentPresentationProgress,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { PresentationIcon } from "../teacher/PresentationIcon";
import {
  liveCodeForSlide,
  PresentationPresentView,
  type PresentationLiveCodeMap,
} from "../teacher/PresentationPresentView";

export default function StudentPresentationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cohortId = searchParams.get("cohortId") ?? "";
  const presentationId = searchParams.get("presentationId") ?? "";
  const classUrl = `/cohorts/${encodeURIComponent(cohortId)}`;
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<Awaited<ReturnType<typeof getStudentPresentation>> | null>(
    null
  );
  const [index, setIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [liveCodeBySlide, setLiveCodeBySlide] = useState<PresentationLiveCodeMap>({});
  const presentRootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!bearer || !cohortId || !presentationId) return;
    setLoading(true);
    setError(null);
    try {
      const ws = await getStudentPresentation(bearer, cohortId, presentationId);
      setWorkspace(ws);
      setIndex(Math.min(ws.lastSlideIndex, Math.max(0, ws.presentation.slides.length - 1)));
      setCompleted(ws.completed);
      setLiveCodeBySlide({});
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load presentation");
    } finally {
      setLoading(false);
    }
  }, [bearer, cohortId, presentationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const goTo = useCallback(
    async (nextIndex: number) => {
      if (!bearer || !workspace) return;
      const slides = workspace.presentation.slides;
      const clamped = Math.max(0, Math.min(slides.length - 1, nextIndex));
      setIndex(clamped);
      try {
        if (clamped >= slides.length - 1) {
          const result = await postStudentPresentationComplete(bearer, cohortId, presentationId, clamped);
          setCompleted(result.completed);
        } else {
          await postStudentPresentationProgress(bearer, cohortId, presentationId, clamped);
        }
      } catch {
        /* keep local index even if progress save fails */
      }
    },
    [bearer, cohortId, presentationId, workspace]
  );

  useEffect(() => {
    if (!bearer || !workspace || loading || completed) return;
    const last = workspace.presentation.slides.length - 1;
    if (last < 0 || index < last) return;
    void postStudentPresentationComplete(bearer, cohortId, presentationId, index)
      .then((result) => setCompleted(result.completed))
      .catch(() => undefined);
  }, [bearer, cohortId, completed, index, loading, presentationId, workspace]);

  useEffect(() => {
    if (!workspace) return;
    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      if (target.closest(".cm-editor") || target.closest("textarea") || target.closest("input")) {
        return true;
      }
      return target.tagName === "TEXTAREA" || target.tagName === "INPUT" || target.isContentEditable;
    }
    function onKey(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      if (e.key === "ArrowRight" || e.key === "PageDown") {
        e.preventDefault();
        void goTo(index + 1);
        return;
      }
      if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        void goTo(index - 1);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goTo, index, workspace]);

  if (!cohortId || !presentationId) {
    return <p className="student-page-inner">Missing presentation.</p>;
  }
  if (loading) return <p className="student-page-inner">Loading slides…</p>;
  if (!workspace) {
    return (
      <div className="student-page-inner">
        <div className="warn-banner" role="alert">
          {error ?? "Presentation not found"}
        </div>
        <p>
          <Link to={classUrl}>Back to class</Link>
        </p>
      </div>
    );
  }

  const slides = workspace.presentation.slides;
  const slide = slides[index];
  const atLast = slides.length > 0 && index >= slides.length - 1;
  const backToClass = () => navigate(classUrl);

  return (
    <div className="presentation-student">
      <header className="presentation-student-head">
        <PresentationIcon
          title={workspace.presentation.title}
          iconName={workspace.presentation.iconName}
          bgColor={workspace.presentation.iconBgColor}
          fgColor={workspace.presentation.iconFgColor}
          size={2.75}
        />
        <div className="presentation-student-head-copy">
          <h1 className="presentation-student-title">{workspace.presentation.title}</h1>
          <p className="presentation-student-lede">
            {workspace.presentation.description ?? "Follow along, and run any code on the slide."}
          </p>
        </div>
        {completed ? <span className="presentation-student-finished">Finished</span> : null}
      </header>
      {slide ? (
        <PresentationPresentView
          variant="embedded"
          requestFullscreen={false}
          autoHideChrome={false}
          title={workspace.presentation.title}
          slide={slide}
          index={index}
          total={slides.length}
          liveCode={liveCodeForSlide(slide, liveCodeBySlide)}
          onLiveCodeChange={(code) => setLiveCodeBySlide((prev) => ({ ...prev, [slide.id]: code }))}
          onResetCode={() =>
            setLiveCodeBySlide((prev) => {
              const next = { ...prev };
              delete next[slide.id];
              return next;
            })
          }
          chromeVisible
          onMouseMove={() => undefined}
          onPrev={() => void goTo(index - 1)}
          onNext={() => void goTo(index + 1)}
          nextLabel={atLast && !completed ? "Mark finished" : "Next"}
          nextDisabled={atLast && completed}
          leadingAction={{ label: "Back to class", onClick: backToClass }}
          consoleHint="Edits stay on this slide until you leave. Ctrl+Enter (or Cmd+Enter) runs."
          presentRootRef={presentRootRef}
        />
      ) : (
        <div className="presentation-student-empty">
          <p className="muted">This presentation has no slides yet.</p>
          <Link className="btn secondary" to={classUrl}>
            Back to class
          </Link>
        </div>
      )}
    </div>
  );
}
